import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';
import {
  useUpgradePromptRules,
  WithEnterpriseManagedOverride,
} from 'app/src/components/UpgradePrompts';

import styles from './CopyBoardFromTemplatePopoverButton.less';

interface Props {
  orgId: string;
  teamName: string;
}

export const CopyBoardFromTemplatePopoverButton: FunctionComponent<Props> = ({
  orgId,
  teamName,
}) => {
  const { isEligible } = useFreeTrialEligibilityRules(orgId, { skip: !orgId });

  const { openPlanSelection } = useUpgradePromptRules(
    orgId,
    `copy-board-from-template-popover-button-${orgId}`,
  );

  const onClick = useCallback(() => {
    openPlanSelection();
    Analytics.sendClickedLinkEvent({
      linkName: 'bcUpgradePrompt',
      source: 'createFromTemplateInlineDialog',
      containers: {
        organization: {
          id: orgId,
        },
      },
      attributes: {
        isFreeTrial: isEligible,
        promptId: 'creatBoardFromTemplatePrompt',
      },
    });
  }, [openPlanSelection, orgId, isEligible]);

  <FormattedMessage
    id="upgrade-path-audit.cannot-copy-board-limit-reached"
    defaultMessage="Looks like this board can't be copied into {teamName} because this free Workspace has hit the maximum number of open boards. For unlimited Workspace boards (and more),"
    description="A message indicating this board cannot be copied because this free Worksapce has hit the maximum number of open boards"
    values={{
      teamName: <span key={'team-name'}>{teamName}</span>,
    }}
  />;
  return (
    <>
      <FormattedMessage
        id="upgrade-path-audit.free-workspace-can-only-have-10-open-boards"
        defaultMessage="Free Workspaces can only have 10 open boards. To add more boards, upgrade your Workspace."
        description="A message indicating that free Workspaces can only have 10 open boards"
        tagName="p"
      />
      <a href="#" onClick={onClick} className={styles.ctaButton}>
        <WithEnterpriseManagedOverride>
          <FormattedMessage
            id="upgrade-path-audit.upgrade-now"
            defaultMessage="Upgrade now"
            description="A call to action to upgrade this Workspace"
          />
        </WithEnterpriseManagedOverride>
      </a>
    </>
  );
};
